import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "../App.css";
import chevronDownIcon from '../asset/chevron-down.svg';

const PersonLogs = ({ username, onLogout, personId, onBack }) => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]); // This will be the same as logs now since backend handles filtering
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [locationFilter, setLocationFilter] = useState(""); // This can still be used if needed for additional filtering
  const [personName, setPersonName] = useState("");
  const [personDetails, setPersonDetails] = useState(null); // Store person details including registered address
  const [expandedRow, setExpandedRow] = useState(null);
  const [openVideo, setOpenVideo] = useState({
    logId: null,
    type: null
  });
  const [exportLoading, setExportLoading] = useState(false);
  const [videoLoading, setVideoLoading] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);

  const [loadingImageId, setLoadingImageId] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  const [showDropdown, setShowDropdown] = useState(null); // for dropdown menu
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [employeeImage, setEmployeeImage] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});

  const API_BASE_URL = `https://${window.location.hostname}`;

  const getAuthHeaders = () => {
    const tabId = sessionStorage.getItem("activeTab");
    const token = sessionStorage.getItem(`token_${tabId}`);
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    };
  };

  // Fetch person details (including registered address and image)
  useEffect(() => {
    const fetchPersonDetails = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/person/${personId}`,
          { method: "GET", headers: getAuthHeaders(), credentials: "include" }
        );

        if (!response.ok) throw new Error("Failed to fetch person details");

        const person = await response.json();
        setPersonDetails(person);
        setPersonName(person.name);
        
        // Fetch employee image from person table
        if (person.imageUrl) {
          setEmployeeImage(person.imageUrl);
        }
      } catch (err) {
        console.error("Error fetching person details:", err);
        setError(err.message);
      }
    };

    if (personId) {
      fetchPersonDetails();
    }
  }, [personId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({
          page,
          limit
        });

        if (fromDate) params.append("fromDate", fromDate);
        if (toDate) params.append("toDate", toDate);
        
        const response = await fetch(
          `${API_BASE_URL}/api/person-logs/${personId}/processed?${params.toString()}`,
          { method: "GET", headers: getAuthHeaders(), credentials: "include" }
        );

        if (!response.ok) throw new Error("Failed to fetch person logs");

        const result = await response.json();
        console.log("Person Logs (filtered by registered address): ", result);

        // Normalize backend keys
        const normalized = (result.data || []).map(log => ({
          ...log,
          imageBase64: log.imageBase64,                 // already full base64 URL
          inputVideoId: null,
          outputVideoId: null,
          timeIn: log.timeIn,                           // backend sends only timeIn
        }));
        
        setLogs(normalized);
        setFilteredLogs(normalized); // No additional filtering needed - backend already did it
        setTotalPages(result.totalPages || 1);

        if (normalized.length > 0 && !personName) {
          setPersonName(normalized[0].name);
        }

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (personId) {
      fetchData();
    }
  }, [personId, page, fromDate, toDate, personName]);

  useEffect(() => {
    setPage(1);
  }, [fromDate, toDate]);

  const fetchVideoIds = async (logId) => {
    const tabId = sessionStorage.getItem("activeTab");
    const token = sessionStorage.getItem(`token_${tabId}`);

    const res = await fetch(
      `${API_BASE_URL}/api/person-logs/${logId}/videos`,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );

    if (!res.ok) return null;
    return res.json(); // { inputVideoId, outputVideoId }
  };
const handlePreview = async (logId) => {
  try {
    setLoadingImageId(logId);

    const res = await fetch(`/api/person-logs/image/${logId}`);
    const data = await res.json();

    setPreviewImage(data.imageUrl);
  } catch (err) {
    console.error(err);
  } finally {
    setLoadingImageId(null);
  }
};
  const handleOpenVideo = async (log, type) => {
    const key = `${log.id}_${type}`;

    setVideoLoading(prev => ({ ...prev, [key]: true }));
    setOpenVideo({ logId: log.id, type });

    try {
      if (
        (type === "input" && log.inputVideoId) ||
        (type === "output" && log.outputVideoId)
      ) {
        return;
      }

      const ids = await fetchVideoIds(log.id);
      if (!ids) return;

      setLogs(prev =>
        prev.map(l =>
          l.id === log.id
            ? { ...l, ...ids }
            : l
        )
      );
    } finally {
      setVideoLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  // Only apply location filter if user manually enters an address filter
  // The core filtering by registered address is already done by backend
  useEffect(() => {
    if (logs && logs.length > 0 && locationFilter) {
      // Apply manual location filter on top of already filtered data
      const f = logs.filter(log =>
        log.address?.toLowerCase().includes(locationFilter.toLowerCase())
      );
      setFilteredLogs(f);
    } else {
      // Show all logs that were already filtered by backend
      setFilteredLogs(logs);
    }
  }, [logs, locationFilter]);

  // ... rest of the component remains the same ...

  const VideoPlayer = ({ videoUrl }) => {
    const [videoSrc, setVideoSrc] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
      if (!videoUrl) return;

      const loadVideo = async () => {
        setLoading(true);
        setError(null);

        try {
          const tabId = sessionStorage.getItem("activeTab");
          const token = sessionStorage.getItem(`token_${tabId}`);

          const response = await fetch(`${API_BASE_URL}${videoUrl}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });

          if (!response.ok) {
            throw new Error(`Failed to load video: ${response.status}`);
          }

          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          setVideoSrc(blobUrl);

        } catch (err) {
          console.error("Video load error:", err);
          setError("Video failed to load");
        } finally {
          setLoading(false);
        }
      };

      loadVideo();

      return () => {
        if (videoSrc) URL.revokeObjectURL(videoSrc);
      };
    }, [videoUrl]);

    if (loading) return <p>Loading video...</p>;
    if (error) return <p className="text-danger">{error}</p>;

    return (
      <video controls width="320" height="200" src={videoSrc} />
    );
  };

  // ======================================================
  // DATE FORMAT
  // ======================================================
  const formatDate = (d) => {
    if (!d) return "N/A";

    // Handle ISO or MySQL datetime
    let clean = d;

    // Remove timezone info if exists
    clean = clean.replace("T", " ");
    clean = clean.replace(".000Z", "");
    clean = clean.replace("Z", "");

    // clean = "2025-12-19 09:01:24"
    const [date, time] = clean.split(" ");

    if (!date || !time) return "Invalid Date";

    const [y, m, day] = date.split("-");
    const [h, min, s] = time.split(":");

    return `${day} ${new Date(y, m - 1).toLocaleString("en-IN", {
      month: "short"
    })} ${y}, ${h}:${min}:${s}`;
  };

  // ======================================================
  // UI
  // ======================================================

  const handleExport = async () => {
    if (!filteredLogs || filteredLogs.length === 0) {
      alert("No logs to export!");
      return;
    }

    setExportLoading(true);

    try {
      const tabId = sessionStorage.getItem("activeTab");
      const token = sessionStorage.getItem(`token_${tabId}`);

      const params = new URLSearchParams();

      if (fromDate) params.append("fromDate", `${fromDate} 00:00:00`);
      if (toDate) params.append("toDate", `${toDate} 23:59:59`);

      if (locationFilter) params.append("address", locationFilter);

      const response = await fetch(
        `${API_BASE_URL}/api/person-logs/${personId}/export?${params.toString()}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `person_logs_${personName.replace(/ /g, "_")}.xlsx`;
      link.click();

    } catch (err) {
      console.error(err);
      alert("Failed to export Excel file!");
    } finally {
      setExportLoading(false);
    }
  };

  // Load image on demand
  const handleLoadImage = (logId, imageBase64) => {
    setLoadedImages(prev => ({
      ...prev,
      [logId]: imageBase64
    }));
  };

  return (
    <div style={{ padding: "20px 16px" }} className="px-md-4">

      {/* HEADER WITH EMPLOYEE INFO */}
      <div className="d-flex flex-column flex-sm-row align-items-center justify-content-between mb-4 p-4 rounded-4 shadow-sm"
        style={{ backgroundColor: "#f9fafd", border: "2px solid #e0e6ff" }}>
        {/* Left side: Image and Employee Info */}
        <div className="d-flex align-items-center">
          {/* Employee Image */}
          <div className="me-3">
            {employeeImage ? (
              <img
                src={employeeImage}
                alt={personName}
                style={{
                  width: 100,
                  height: 100,
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "3px solid #4B6BFB",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.15)"
                }}
              />
            ) : (
              <div
                style={{
                  width: 100,
                  height: 100,
                  borderRadius: "50%",
                  backgroundColor: "#4B6BFB",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "2.5rem",
                  color: "#fff",
                  fontWeight: "bold",
                  border: "3px solid #4B6BFB",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.15)"
                }}
              >
                {personName ? personName.charAt(0).toUpperCase() : "P"}
              </div>
            )}
          </div>
          
          {/* Employee Name and Address */}
          <div className="d-flex flex-column">
            <h4 className="fw-bold text-success mb-1">Person Logs</h4>
            <h3 className="text-dark fw-semibold mb-2">{personName || "Loading..."}</h3>
            {personDetails && personDetails.address && (
              <small className="text-muted">
                <i className="fas fa-map-marker-alt me-1"></i>
                {personDetails.address}
              </small>
            )}
          </div>
        </div>
        
        {/* Right side: Back button */}
        <div>
          <button 
            className="btn btn-outline-secondary"
            onClick={onBack}
          >
            <i className="fas fa-arrow-left me-2"></i>Back
          </button>
        </div>
      </div>

      {/* FILTERS */}
      <div className="rounded-4 shadow-lg p-4 mb-4">
        <div className="row text-muted fw-semibold mb-2">
          <div className="col-md-2">From Date</div>
          <div className="col-md-2">To Date</div>
          <div className="col-md-2 text-center">Export</div>
          <div className="col-md-1 text-center">Input Video</div>
          <div className="col-md-1 text-center">Output Video</div>
          <div className="col-md-1 text-center">Images</div>
        </div>

        <div className="row g-2">
          <div className="col-md-2">
            <input type="date" className="form-control"
              value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>

          <div className="col-md-2">
            <input type="date" className="form-control"
              value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>

          <div className="col-md-2 text-center">
            <button className="btn btn-success w-100" onClick={handleExport}>EXPORT</button>
          </div>

          <div className="col-md-1 text-center">{filteredLogs.filter(l => l.inputVideoId).length}</div>
          <div className="col-md-1 text-center">{filteredLogs.filter(l => l.outputVideoId).length}</div>
          <div className="col-md-1 text-center">{filteredLogs.filter(l => l.imageBase64).length}</div>
        </div>
      </div>

      {/* TABLE */}
      <div className="rounded-4 shadow-lg p-4 bg-white">

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-success"></div>
            <p>Loading logs...</p>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="text-center py-4 text-muted">No logs found for the registered address.</div>
        ) : (

          <div className="table-responsive">
            <table className="table table-hover align-middle">

              <thead className="text-white" style={{ background: "linear-gradient(90deg, #4B6BFB, #8C6BFB)" }}>
                <tr>
                  <th>ID</th>
                  <th>Address</th>
                  <th>Punching Status</th>
                  <th>Time</th>
                  <th>Image</th>
                  <th>Videos</th>
                </tr>
              </thead>

              <tbody>
                {filteredLogs.map(log => (
                  <React.Fragment key={log.id}>

                    {/* MAIN ROW */}
                    <tr
                      onClick={() => {
                        if (!log.id) return;
                        setExpandedRow(expandedRow === log.id ? null : log.id);
                        setOpenVideo({ logId: null, type: null });
                      }}
                      style={{
                        cursor: "pointer",
                        backgroundColor:
                          log.timeType === "IN" ? "#d1fae5" :
                            log.timeType === "OUT" ? "#fee2e2" :
                              log.timeType === "MID" ? "#fef3c7" : "transparent"
                      }}
                    >
                      <td>#{log.id}</td>
                      <td>{log.address}</td>
                      {/* <td>{log.timeType === "IN" ? "Punch IN" : "Punch Out"}</td> */}
                      <td>
                        {log.timeType === "IN"  ? <span className="badge bg-success">Punch IN</span>  :
                         log.timeType === "OUT" ? <span className="badge bg-danger">Punch Out</span> :
                                                  <span className="text-muted">—</span>}
                      </td>
                      <td>{formatDate(log.timeIn)}</td>

                      <td>
                        {log.imageBase64 && (
                          <button
                            className="btn btn-sm btn-outline-primary"
                            style={{ borderRadius: "8px", padding: "6px 12px" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLoadImage(log.id, log.imageBase64);
                              setPreviewImage(log.imageBase64);
                            }}
                          >
                            <i className="fas fa-image me-1"></i>Preview
                          </button>
                        )}
                      </td>
                      {/* <td>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          style={{ borderRadius: "8px", padding: "6px 12px" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePreview(log.id);
                          }}
                        >
                          {loadingImageId === log.id ? (
                            "Loading..."
                          ) : (
                            <>
                              <i className="fas fa-image me-1"></i>Preview
                            </>
                          )}
                        </button>
                      </td> */}
                      <td>
                        <div className="d-flex align-items-center justify-content-end" style={{ gap: "10px", paddingRight: "20px" }}>
                          <span className="badge bg-secondary px-3 py-2" style={{ fontSize: "12px" }}>
                            Play Video
                          </span>
                          <button
                            className="btn btn-sm p-1"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedRow(expandedRow === log.id ? null : log.id);
                              setOpenVideo({ logId: null, type: null });
                            }}
                            style={{ border: 'none', background: 'transparent' }}
                            title="Expand to view videos"
                          >
                            <img src={chevronDownIcon} alt="expand" style={{ width: '12px', height: '12px' }} />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* EXPANDED ROW */}
                    {expandedRow === log.id && (
                      <tr>
                        <td colSpan="6" className="bg-light p-3">

                          <div className="d-flex gap-3 mb-3 align-items-center">
                            <button
                              className="btn btn-outline-success btn-sm"
                              onClick={() => {
                                handleOpenVideo(log, "input")
                              }}
                            >
                              INPUT Video
                            </button>

                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => {
                                handleOpenVideo(log, "output")
                              }}
                            >
                              OUTPUT Video
                            </button>
                          </div>

                          {/* INPUT VIDEO */}
                          {openVideo.logId === log.id && openVideo.type === "input" && (
                            videoLoading[`${log.id}_input`] ? (
                              <p className="text-muted">Loading INPUT video...</p>
                            ) : (
                              log.inputVideoId ? (
                                <VideoPlayer videoUrl={`/api/videos/input/${log.inputVideoId}`} />
                              ) : (
                                <p className="text-danger fw-semibold">No INPUT video available</p>
                              )
                            )
                          )}

                          {/* OUTPUT VIDEO */}
                          {openVideo.logId === log.id && openVideo.type === "output" && (
                            videoLoading[`${log.id}_output`] ? (
                              <p className="text-muted">Loading OUTPUT video...</p>
                            ) : (
                              log.outputVideoId ? (
                                <VideoPlayer videoUrl={`/api/videos/output/${log.outputVideoId}`} />
                              ) : (
                                <p className="text-danger fw-semibold">No OUTPUT video available</p>
                              )
                            )
                          )}

                        </td>
                      </tr>
                    )}

                  </React.Fragment>
                ))}
              </tbody>

            </table>
          </div>

        )}

      </div>
      {/* PAGINATION */}
      <div className="d-flex justify-content-center mt-4 gap-2">
        <button
          className="btn btn-outline-secondary btn-sm"
          disabled={page === 1}
          onClick={() => setPage(prev => prev - 1)}
        >
          Previous
        </button>

        {[...Array(totalPages)].map((_, i) => (
          <button
            key={i}
            className={`btn btn-sm ${page === i + 1 ? "btn-success" : "btn-outline-success"
              }`}
            onClick={() => setPage(i + 1)}
          >
            {i + 1}
          </button>
        ))}

        <button
          className="btn btn-outline-secondary btn-sm"
          disabled={page === totalPages}
          onClick={() => setPage(prev => prev + 1)}
        >
          Next
        </button>
      </div>

      {/* IMAGE PREVIEW MODAL */}
      {previewImage && (
        <div
          onClick={() => setPreviewImage(null)}
          style={{
            position: "fixed",
            top: 0, left: 0,
            width: "100%", height: "100%",
            background: "rgba(0,0,0,0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999
          }}
        >
          <img
            src={previewImage}
            style={{ maxWidth: "90%", maxHeight: "90%", borderRadius: 12 }}
            alt="preview"
          />
        </div>
      )}

    </div>
  );
};

export default PersonLogs;